import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { isOpenEndDate } from "../utils/open_end_date"

/**
 * Controls task/event bar resizing (extend/shorten) in the Plantafel.
 *
 * For tasks in 3-day / week view: uses the integer Day+Part grid model
 * (parts 1-5 within each day, 5 parts per day) shared with
 * `plantafel_task_drag_controller`. Math is integer-only — no Date parsing,
 * no fractional positions, no view-start-date arithmetic on the client.
 * Visual preview is a separate ghost overlay + guide line so the real bar
 * is never mutated until the server confirms the save.
 *
 * For other resources (sick_notes, leave_requests, appointments) and tasks
 * outside 3-day/week view: falls back to day-only mode (still day-integer).
 */
export default class extends Controller {
  static targets = ["leftHandle", "rightHandle", "bar"]
  static values = {
    resourceId: Number,
    resourceType: { type: String, default: "task" },
    startDate: String,
    endDate: String,
    viewMode: String,
    viewDays: Number,
    // Legacy taskId support
    taskId: Number,
    // Day+Part integer position (1-indexed parts, e.g., startPart=1 means the first part of startDay)
    startDay: Number,
    startPart: Number,
    endDay: Number,
    endPart: Number,
  }

  connect() {
    this.partsPerDay = 5
    this.isResizing = false
    this.direction = null
    this.didInitiateResize = false
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleMouseUp = this.handleMouseUp.bind(this)
  }

  disconnect() {
    document.removeEventListener("mousemove", this.handleMouseMove)
    document.removeEventListener("mouseup", this.handleMouseUp)
    // If this controller owned the in-flight resize (Turbo replaced our wrapper
    // mid-cleanup), reset the body cursor/userSelect immediately so the new
    // controller starts in a clean visual state. The `plantafelResizing` body
    // flag is intentionally left alone — it's owned by a window-level timeout
    // that survives the controller swap and provides a grace period after the
    // drag so adjacent "+" hover overlays don't flicker.
    if (this.didInitiateResize) {
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
    this.removeGhost()
    this.removeConflictTooltip()
  }

  // ==================== ENTRY POINTS ====================

  startResizeLeft(event) {
    event.preventDefault()
    event.stopPropagation()
    this.rememberActiveBar(event)
    this.startResize(event, "left")
  }

  startResizeRight(event) {
    event.preventDefault()
    event.stopPropagation()
    this.rememberActiveBar(event)
    // An open-ended period ("Offenes Ende", 31.12.9999) has no right edge to drag: the delta
    // would land past 9999 and the save fails, and the server keeps the open end anyway
    // (PlantafelController#keep_open_end). Its end date is set in the task form.
    if (this.openEnded) return

    this.startResize(event, "right")
  }

  get openEnded() {
    return isOpenEndDate(this.currentEndDate)
  }

  // A task worked in several periods draws one bar per period. The gesture edits the
  // bar it started on, so its dates and its period id come from that element rather
  // than from the task-level values on the row wrapper.
  rememberActiveBar(event) {
    this.activeBar = event.target.closest('[data-plantafel-resize-target="bar"]')
  }

  preventClick(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  // ==================== DRAG LIFECYCLE ====================

  startResize(event, direction) {
    this.direction = direction
    this.startX = event.clientX
    this.currentPartsChange = 0
    this.lastPartsChange = 0

    const bar = this.getBar()
    if (!bar) return

    // Measure the container width once — used to translate pixel deltas to parts.
    // `this.element` is the absolute-inset-0 wrapper that fills the lane row,
    // so its width matches the server-rendered 100% basis for the bar's left%/width%.
    const wrapperWidth = this.element.offsetWidth
    if (!wrapperWidth) return

    this.timePrecision = this.useTimePrecision()
    this.viewDays = this.resolveViewDays()
    this.partsPerView = this.timePrecision ? this.viewDays * this.partsPerDay : this.viewDays
    this.partWidthPx = wrapperWidth / this.partsPerView

    if (this.timePrecision) {
      // Time-precision Day+Part mode (tasks in 3-day/week view).
      // 0-based inclusive start, 0-based exclusive end (so end - start = duration in parts).
      const bar = this.activeBar?.dataset
      const startDay =
        bar?.segmentStartDay !== undefined ? Number(bar.segmentStartDay) : this.startDayValue
      const startPart =
        bar?.segmentStartPart !== undefined ? Number(bar.segmentStartPart) : this.startPartValue
      const endDay = bar?.segmentEndDay !== undefined ? Number(bar.segmentEndDay) : this.endDayValue
      const endPart =
        bar?.segmentEndPart !== undefined ? Number(bar.segmentEndPart) : this.endPartValue
      this.startTotalParts = startDay * this.partsPerDay + (startPart - 1)
      this.endTotalParts = endDay * this.partsPerDay + endPart
    } else {
      // Day-only mode (legacy resources or tasks without working hours).
      // Anchor `dayOnlyAnchor` is the resource's original start date — used at
      // save time to convert the proposed delta back into an absolute date.
      const start = new Date(this.currentStartDate)
      if (isNaN(start.getTime())) return
      this.dayOnlyAnchor = start
      // Initialize the view-relative start/end from the bar's measured offsets,
      // so the ghost overlays the real bar at its current position.
      this.startTotalParts = Math.round((bar.offsetLeft / wrapperWidth) * this.partsPerView)
      this.endTotalParts = Math.round(
        ((bar.offsetLeft + bar.offsetWidth) / wrapperWidth) * this.partsPerView,
      )
      if (this.endTotalParts <= this.startTotalParts) this.endTotalParts = this.startTotalParts + 1
      this.initialStartTotalParts = this.startTotalParts
      this.initialEndTotalParts = this.endTotalParts
    }

    this.isResizing = true
    this.didInitiateResize = true
    this.laneSiblingRanges = this.collectLaneSiblings()

    bar.classList.add("ring-2", "ring-bautask-green/50")
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    document.body.dataset.plantafelResizing = "true"

    this.createGhost()
    this.updateGhost(this.startTotalParts, this.endTotalParts, false)

    document.addEventListener("mousemove", this.handleMouseMove)
    document.addEventListener("mouseup", this.handleMouseUp)
  }

  handleMouseMove(event) {
    if (!this.isResizing) return

    const deltaX = event.clientX - this.startX
    const partsChange = Math.round(deltaX / this.partWidthPx)
    if (partsChange === this.lastPartsChange) return
    this.lastPartsChange = partsChange

    const proposed = this.proposedRange(partsChange)
    this.currentPartsChange = proposed.partsChange

    const overlap = this.overlapsLaneSibling(proposed.startTotalParts, proposed.endTotalParts)
    this.updateGhost(proposed.startTotalParts, proposed.endTotalParts, overlap)

    if (this.currentResourceType === "task") {
      this.debouncedCheckConflicts(proposed)
    }
  }

  handleMouseUp() {
    if (!this.isResizing) return

    const bar = this.getBar()
    bar?.classList.remove("ring-2", "ring-bautask-green/50")
    document.body.style.cursor = ""
    document.body.style.userSelect = ""

    document.removeEventListener("mousemove", this.handleMouseMove)
    document.removeEventListener("mouseup", this.handleMouseUp)

    this.isResizing = false
    this.removeConflictTooltip()

    // Schedule a window-level cleanup that outlives this controller (the Turbo
    // Stream response replaces our wrapper, disconnecting us). Each new resize
    // cancels any pending cleanup from a prior one so the flag stays "true"
    // for the full grace period after the *most recent* drag — keeping the
    // "+ create task" hover overlays suppressed while the user's cursor is
    // still in the area.
    if (window.__plantafelResizeCleanup) {
      clearTimeout(window.__plantafelResizeCleanup)
    }
    window.__plantafelResizeCleanup = setTimeout(() => {
      delete document.body.dataset.plantafelResizing
      window.__plantafelResizeCleanup = null
    }, 800)

    if (this.currentPartsChange === 0) {
      this.removeGhost()
      this.activeBar = null
      return
    }

    const proposed = this.proposedRange(this.currentPartsChange)
    // activeBar is cleared with the ghost: keeping it past the gesture would let
    // getBar()/currentTimespanId read a stale (possibly detached) element after
    // a partial re-render. save() reads it synchronously before the request.
    this.save(proposed).finally(() => {
      this.removeGhost()
      this.activeBar = null
    })
  }

  // ==================== PROPOSED RANGE (clamped, snapped) ====================

  proposedRange(rawPartsChange) {
    let partsChange = rawPartsChange
    let startTotalParts = this.startTotalParts
    let endTotalParts = this.endTotalParts

    // Time-precision mode clamps to the visible view; day-only mode (legacy
    // resources) anchors at the original start, and extending past the anchor
    // is allowed (saves earlier `date_from`).
    const minStart = this.timePrecision ? 0 : Number.NEGATIVE_INFINITY
    const maxEnd = this.timePrecision ? this.partsPerView : Number.POSITIVE_INFINITY

    if (this.direction === "left") {
      startTotalParts += partsChange
      if (startTotalParts < minStart) {
        partsChange += minStart - startTotalParts
        startTotalParts = minStart
      }
      if (startTotalParts > endTotalParts - 1) {
        partsChange -= startTotalParts - (endTotalParts - 1)
        startTotalParts = endTotalParts - 1
      }
    } else {
      endTotalParts += partsChange
      if (endTotalParts > maxEnd) {
        partsChange -= endTotalParts - maxEnd
        endTotalParts = maxEnd
      }
      if (endTotalParts < startTotalParts + 1) {
        partsChange += startTotalParts + 1 - endTotalParts
        endTotalParts = startTotalParts + 1
      }
    }

    return { partsChange, startTotalParts, endTotalParts }
  }

  // ==================== GHOST + GUIDE PREVIEW ====================

  createGhost() {
    this.removeGhost()

    const ghost = document.createElement("div")
    ghost.className =
      "plantafel-resize-ghost absolute top-1 bottom-1 rounded border-2 border-dashed pointer-events-none z-20"
    ghost.dataset.plantafelResizeGhost = "true"

    const guide = document.createElement("div")
    guide.className = "plantafel-resize-guide absolute top-0 bottom-0 w-px pointer-events-none z-20"
    guide.dataset.plantafelResizeGuide = "true"

    const label = document.createElement("div")
    label.className =
      "plantafel-resize-label absolute -top-6 px-1.5 py-0.5 text-[10px] font-medium rounded shadow-xs pointer-events-none z-30 whitespace-nowrap"
    label.dataset.plantafelResizeLabel = "true"

    this.element.appendChild(ghost)
    this.element.appendChild(guide)
    this.element.appendChild(label)

    this.ghostEl = ghost
    this.guideEl = guide
    this.labelEl = label
  }

  updateGhost(startTotalParts, endTotalParts, isOverlap) {
    if (!this.ghostEl) return

    const partsPerView = this.partsPerView
    const leftPercent = (startTotalParts / partsPerView) * 100
    const widthPercent = ((endTotalParts - startTotalParts) / partsPerView) * 100
    const edgePercent = this.direction === "left" ? leftPercent : leftPercent + widthPercent

    this.ghostEl.style.left = `${leftPercent}%`
    this.ghostEl.style.width = `${widthPercent}%`
    this.guideEl.style.left = `${edgePercent}%`

    const okBg = "rgba(0, 156, 114, 0.18)"
    const okBorder = "#009C72"
    const warnBg = "rgba(232, 79, 79, 0.20)"
    const warnBorder = "#E84F4F"

    this.ghostEl.style.backgroundColor = isOverlap ? warnBg : okBg
    this.ghostEl.style.borderColor = isOverlap ? warnBorder : okBorder
    this.guideEl.style.backgroundColor = isOverlap ? warnBorder : okBorder

    if (this.labelEl) {
      this.labelEl.style.left = `${edgePercent}%`
      this.labelEl.style.transform = "translateX(-50%)"
      this.labelEl.style.backgroundColor = isOverlap ? warnBorder : okBorder
      this.labelEl.style.color = "white"
      this.labelEl.textContent = this.formatRangeLabel(startTotalParts, endTotalParts)
    }
  }

  removeGhost() {
    this.ghostEl?.remove()
    this.guideEl?.remove()
    this.labelEl?.remove()
    this.ghostEl = null
    this.guideEl = null
    this.labelEl = null
  }

  formatRangeLabel(startTotalParts, endTotalParts) {
    if (this.timePrecision) {
      const startDay = Math.floor(startTotalParts / this.partsPerDay) + 1
      const endDay = Math.floor((endTotalParts - 1) / this.partsPerDay) + 1
      const widthParts = endTotalParts - startTotalParts
      return startDay === endDay
        ? `Tag ${startDay} · ${widthParts}/5`
        : `Tag ${startDay} → Tag ${endDay}`
    }
    const days = endTotalParts - startTotalParts
    return `${days} ${days === 1 ? "Tag" : "Tage"}`
  }

  // ==================== LANE-SIBLING OVERLAP ====================

  collectLaneSiblings() {
    // Siblings in the lane wrapper are other task wrappers with the same
    // start-day/start-part/end-day/end-part attribute set.
    const lane = this.element.parentElement
    if (!lane) return []

    const ranges = []
    for (const sibling of lane.children) {
      if (sibling === this.element) continue
      const startDay = parseInt(sibling.dataset.plantafelResizeStartDayValue, 10)
      const startPart = parseInt(sibling.dataset.plantafelResizeStartPartValue, 10)
      const endDay = parseInt(sibling.dataset.plantafelResizeEndDayValue, 10)
      const endPart = parseInt(sibling.dataset.plantafelResizeEndPartValue, 10)
      if (
        Number.isNaN(startDay) ||
        Number.isNaN(startPart) ||
        Number.isNaN(endDay) ||
        Number.isNaN(endPart)
      ) {
        continue
      }
      ranges.push({
        startTotalParts: startDay * this.partsPerDay + (startPart - 1),
        endTotalParts: endDay * this.partsPerDay + endPart,
      })
    }
    return ranges
  }

  overlapsLaneSibling(startTotalParts, endTotalParts) {
    return this.laneSiblingRanges.some(
      (range) => range.endTotalParts > startTotalParts && endTotalParts > range.startTotalParts,
    )
  }

  // ==================== SAVE ====================

  async save(proposed) {
    if (this.timePrecision) {
      return this.saveDayPart(proposed)
    }
    return this.saveDayOnly(proposed)
  }

  async saveDayPart(proposed) {
    const { startTotalParts, endTotalParts } = proposed

    // A task that began before the visible week keeps a negative start when only its
    // right edge is dragged. JS `%` keeps the dividend's sign, so the part has to be
    // taken as the floor remainder or it comes out negative (day -3, part -3).
    const startDay = Math.floor(startTotalParts / this.partsPerDay)
    const startPart = startTotalParts - startDay * this.partsPerDay + 1
    // endTotalParts is exclusive; we want the inclusive 1-indexed end part.
    const endDay = Math.floor((endTotalParts - 1) / this.partsPerDay)
    const endPart = endTotalParts - endDay * this.partsPerDay

    const body = {
      task: { start_day: startDay, start_part: startPart, end_day: endDay, end_part: endPart },
      ...this.viewContext(),
    }

    return this.patch(`/plantafel/tasks/${this.currentResourceId}/schedule`, body)
  }

  async saveDayOnly(proposed) {
    const { startTotalParts, endTotalParts } = proposed
    if (!this.dayOnlyAnchor) return

    // Apply the delta from the resource's original start, expressed in days.
    const startDelta = startTotalParts - this.initialStartTotalParts
    const endDelta = endTotalParts - this.initialEndTotalParts

    const start = new Date(this.dayOnlyAnchor)
    start.setDate(start.getDate() + startDelta)

    const originalEnd = new Date(this.currentEndDate)
    const end = new Date(originalEnd)
    end.setDate(end.getDate() + endDelta)

    const { url, body } = this.buildDayOnlyRequest(start, end)
    return this.patch(url, body)
  }

  async patch(url, body) {
    const bar = this.getBar()
    if (bar) {
      bar.style.opacity = "0.6"
      bar.style.pointerEvents = "none"
    }

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify(body),
      })

      if (response.ok) {
        const contentType = response.headers.get("Content-Type") || ""
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }
        // Bar is replaced by Turbo Stream; no need to restore styles.
        return
      }

      // A refused change comes back as a notification stream saying why — a task with
      // several periods cannot be moved as a whole, for instance. Showing the generic
      // flash instead would throw that explanation away.
      const errorContentType = response.headers.get("Content-Type") || ""
      if (errorContentType.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(await response.text())
      } else {
        this.flashError()
      }

      // 422 is the server telling the user no, not a fault worth reporting.
      if (response.status !== 422) {
        Honeybadger.notify(`Plantafel resize save failed (${response.status})`, {
          context: { component: "plantafel-resize", url },
        })
      }
      this.restoreBarState(bar)
    } catch (error) {
      Honeybadger.notify(error, { context: { component: "plantafel-resize", url } })
      this.flashError()
      this.restoreBarState(bar)
    }
  }

  restoreBarState(bar) {
    if (!bar) return
    bar.style.opacity = ""
    bar.style.pointerEvents = ""
  }

  buildDayOnlyRequest(startDate, endDate) {
    const startStr = this.formatDateForApi(startDate)
    const endStr = this.formatDateForApi(endDate)
    const resourceId = this.currentResourceId

    switch (this.currentResourceType) {
      case "sick_note":
        return {
          url: `/plantafel/sick_notes/${resourceId}/schedule`,
          body: { sick_note: { date_from: startStr, date_to: endStr } },
        }
      case "leave_request":
        return {
          url: `/plantafel/leave_requests/${resourceId}/schedule`,
          body: { leave_request: { date_from: startStr, date_to: endStr } },
        }
      case "appointment":
        return {
          url: `/plantafel/appointments/${resourceId}/schedule`,
          body: { appointment: { date_from: startStr, date_to: endStr } },
        }
      default:
        return {
          url: `/plantafel/tasks/${resourceId}/schedule`,
          body: {
            task: { start_date: startStr, end_date: endStr, timespan_id: this.currentTimespanId },
            ...this.viewContext(),
          },
        }
    }
  }

  // ==================== CONFLICT TOOLTIP ====================

  debouncedCheckConflicts(proposed) {
    if (this.conflictCheckTimeout) clearTimeout(this.conflictCheckTimeout)
    this.conflictCheckTimeout = setTimeout(() => this.checkConflicts(proposed), 300)
  }

  async checkConflicts(proposed) {
    if (this.currentResourceType !== "task") return

    const dates = this.proposedDates(proposed)
    if (!dates) return

    try {
      const response = await fetch(
        `/plantafel/tasks/${this.currentResourceId}/check_conflicts?start_date=${dates.startStr}&end_date=${dates.endStr}`,
        { method: "GET", headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken } },
      )

      if (!response.ok) return

      const data = await response.json()
      if (data.conflicts?.length > 0) {
        this.showConflictTooltip(data.conflicts)
      } else {
        this.removeConflictTooltip()
      }
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-resize", action: "check_conflicts" },
      })
    }
  }

  proposedDates(proposed) {
    if (this.timePrecision) {
      // We can't compute calendar dates client-side without the view start;
      // skip conflict-check for time-precision mode. The server is the
      // source of truth for dates; conflicts will still be detected at save.
      return null
    }

    if (!this.dayOnlyAnchor) return null
    const startDelta = proposed.startTotalParts - this.initialStartTotalParts
    const endDelta = proposed.endTotalParts - this.initialEndTotalParts
    const start = new Date(this.dayOnlyAnchor)
    start.setDate(start.getDate() + startDelta)
    const end = new Date(this.currentEndDate)
    end.setDate(end.getDate() + endDelta)

    return { startStr: this.formatDateForApi(start), endStr: this.formatDateForApi(end) }
  }

  showConflictTooltip(conflicts) {
    this.removeConflictTooltip()
    if (!this.ghostEl) return

    const tooltip = document.createElement("div")
    tooltip.className =
      "fixed z-100 bg-yellow-50 border border-yellow-400 rounded-lg px-3 py-2 shadow-lg max-w-xs"
    tooltip.id = "resize-conflict-tooltip"

    const conflictHtml = conflicts
      .map(
        (c) => `
        <div class="flex items-start gap-2 text-sm">
          <svg class="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <span class="text-yellow-800">${c.title}</span>
        </div>`,
      )
      .join("")

    const label = this.getTranslation("conflictsDetected", "Konflikte erkannt:")
    tooltip.innerHTML = `<div class="font-medium text-yellow-800 mb-1">${label}</div>${conflictHtml}`

    const rect = this.ghostEl.getBoundingClientRect()
    tooltip.style.left = `${rect.left}px`
    tooltip.style.top = `${rect.bottom + 8}px`

    document.body.appendChild(tooltip)
    this.conflictTooltip = tooltip
  }

  removeConflictTooltip() {
    this.conflictTooltip?.remove()
    this.conflictTooltip = null
    document.getElementById("resize-conflict-tooltip")?.remove()
  }

  // ==================== HELPERS ====================

  useTimePrecision() {
    if (this.currentResourceType !== "task") return false
    const viewMode = this.viewModeValue || this.detectViewMode()
    if (!["3_days", "week"].includes(viewMode)) return false
    // Only use time precision when the server rendered the Day+Part data —
    // i.e., the task has scheduled times. Otherwise fall back to day-only.
    return this.hasStartDayValue && this.hasEndDayValue
  }

  resolveViewDays() {
    if (this.hasViewDaysValue && this.viewDaysValue > 0) return this.viewDaysValue
    const viewMode = this.viewModeValue || this.detectViewMode()
    if (viewMode === "3_days") return 3
    return 7
  }

  detectViewMode() {
    const plantafelContainer = document.querySelector('[data-controller*="plantafel"]')
    return plantafelContainer?.dataset.plantafelViewModeValue || "week"
  }

  getBar() {
    if (this.activeBar) return this.activeBar

    return this.hasBarTarget
      ? this.barTarget
      : this.element.querySelector('[data-plantafel-resize-target="bar"]') || this.element
  }

  // Falls back to the row's own dates for a task that draws a single bar.
  get currentStartDate() {
    return this.activeBar?.dataset.segmentStartDate || this.startDateValue
  }

  get currentEndDate() {
    return this.activeBar?.dataset.segmentEndDate || this.endDateValue
  }

  get currentTimespanId() {
    return this.activeBar?.dataset.timespanId || ""
  }

  get currentResourceId() {
    return this.hasResourceIdValue ? this.resourceIdValue : this.taskIdValue
  }

  get currentResourceType() {
    return this.resourceTypeValue || "task"
  }

  get csrfToken() {
    return document.head.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
  }

  viewContext() {
    const plantafelContainer = document.querySelector('[data-controller="plantafel"]')
    const viewMode = plantafelContainer?.dataset.plantafelViewModeValue || "week"
    const urlParams = new URLSearchParams(window.location.search)
    const viewStartDate = urlParams.get("date") || this.formatDateForApi(new Date())
    return { view_mode: viewMode, view_start_date: viewStartDate }
  }

  formatDateForApi(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  getTranslation(key, fallback) {
    const plantafelContainer = document.querySelector('[data-controller*="plantafel"]')
    if (plantafelContainer) {
      const attr = `plantafel${key.charAt(0).toUpperCase()}${key.slice(1)}Value`
      const value = plantafelContainer.dataset[attr]
      if (value) return value
    }
    return fallback
  }

  flashError() {
    const message = this.getTranslation("errorSave", "Fehler beim Speichern der Änderungen")
    const notification = document.createElement("div")
    notification.className =
      "fixed top-4 right-4 z-100 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg"
    notification.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      </div>
    `
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }
}
